import { Component, ViewChild, AfterViewInit  } from '@angular/core';

import { CountdownComponent } from './countdown/countdown.component';
import { Question } from './question';
import { QuestionService } from './question.service';
import { GpioService } from './gpio.service';
import { KeyboardService } from './keyboard.service';
import { ConfigService } from './config.service';
import { KeyCode } from './keycode.interface';
import { ButtonCode } from './buttoncode.interface';
import { ConsoleService } from './console.service';
import { Indicators } from './indicators.interface';

const Audio = window['Audio'];

enum GameState {
  Attract,
  Countdown,
  Question,
  Correct,
  Details
};

const KeyMap: Map<KeyCode, ButtonCode> = new Map([
  [KeyCode.KeyA, ButtonCode.Red1],
  [KeyCode.KeyS, ButtonCode.Yellow1],
  [KeyCode.KeyD, ButtonCode.Blue1],
]);


@Component({
  selector: 'app-root',
  templateUrl: './app.component.html',
  styleUrls: ['./app.component.scss']
})
export class AppComponent implements AfterViewInit {
  @ViewChild(CountdownComponent, { static: true }) countdown;

  public GameState = GameState;
  public state = GameState.Attract;
  public questionNumber: number = 0;
  public debugEnabled: boolean = false;
  public hardMode: boolean;
  private totalQuestions: number = 3;
  private detailTime: number = 5000;

  private timer: any;
  private deck: Question[];

  public indicators: Indicators = {
    red: false,
    yellow: false,
    blue: false
  };

  private audio = new Audio();


  constructor(
    private questionService: QuestionService,
    private gpioService: GpioService,
    private keyboardService: KeyboardService,
    private configService: ConfigService,
    private consoleService: ConsoleService
  ) { }

  ngAfterViewInit() {
    this.configService.getSettings().subscribe(settings => {
      this.debugEnabled = settings.debug;
      this.consoleService.log('Debug mode: ' + (this.debugEnabled ? 'enabled' : 'disabled'));

      this.totalQuestions = settings.questions;
      this.detailTime = settings.detail_delay * 1000;
    });


    // Initialize the card deck once upon init - loads the questions from Excel file into memory.
    this.questionService.loadDeck();


    this.keyboardService.any(Array.from(KeyMap.keys())).subscribe(event => {
      // For simplicity, remap keyboard keys to specific GPIO buttons and invoke the same GPIO processing.
      this.onButton(KeyMap.get(event.code as KeyCode));
    });


    this.keyboardService.all(Array.from(KeyMap.keys()), 1500).subscribe(() => {
      if (this.playing) {
        this.consoleService.log('Stopping per request (keyboard)');
        this.stop();
      }
    });


    this.gpioService.any([
      ButtonCode.Red1,
      ButtonCode.Yellow1,
      ButtonCode.Blue1,
    ]).subscribe(event => {
      this.onButton(event.code);
    });


    this.gpioService.all([
      ButtonCode.Red1,
      ButtonCode.Yellow1,
      ButtonCode.Blue1,
    ], 1500).subscribe(() => {
      if (this.playing) {
        this.consoleService.log('Stopping per request (GPIO)');
        this.stop();
      }
    });


    // Test to throw a warning when all of the buttons are pressed and held for a strangely long period of time.
    // This could possibly indicate a fault in the wiring (NC rather than NO contacts).
    this.gpioService.all([
      ButtonCode.Red1,
      ButtonCode.Yellow1,
      ButtonCode.Blue1
    ], 10000).subscribe(() => {
      this.consoleService.log('Odd, all buttons held for 10 seconds?  Looks like a wiring issue...');
    });


    this.consoleService.log('Initialized');
  }


  get playing(): boolean {
    return this.state === GameState.Question || this.state === GameState.Correct || this.state === GameState.Details;
  }

  get current(): Question {
    return this.deck ? this.deck[0] : null;
  }

  get final(): boolean {
    return this.questionNumber >= this.totalQuestions;
  }


  play(type: string): void {
    const filename = type + '.mp3';
    this.audio.src = 'assets/sounds/' + filename;
    this.audio.play().then(_ => {
      this.consoleService.log('Playing audio: ' + filename);
    }).catch(error => {
      this.consoleService.error('Failed to play audio' + filename + ': ' + error);
    });
  }


  startInactivityTimer(): void {
    // Stop the existing timer, if it's running.
    if (this.timer) {
      clearTimeout(this.timer);
    }

    // Return to the attract screen after a long period of inactivity.
    this.timer = setTimeout(() => {
      this.state = GameState.Attract;
    }, 60000);
  }


  start(hardMode: boolean) {
    if (this.state === GameState.Attract) {
      this.consoleService.log('Starting game using ' + (hardMode ? 'hard' : 'easy') + ' questions.');
      this.hardMode = hardMode;

      this.shuffle();
      this.indicators.red = false;
      this.indicators.yellow = false;
      this.indicators.blue = false;

      this.state = GameState.Countdown;
      this.questionNumber = 0;
      this.countdown.start();
    }
  }


  shuffle() {
    this.deck = this.questionService.getDeck(this.hardMode ? 'Hard' : 'Easy');
  }


  onCountdownComplete(): void {
    if (this.state === GameState.Countdown) {
      this.state = GameState.Question;
      this.nextQuestion();
    }
  }

  /* Advance to the next question in the deck.  Called when both players have managed to get the correct answer or
   * when the timer has expired. */
  nextQuestion(): void {
    if (this.playing) {
      this.state = GameState.Question;
      this.questionNumber += 1;

      // Check to see if there are any more questions remaining in the deck.
      if (this.deck.length == 0) {
        // When out of questions, reshuffle the deck.
        this.shuffle();
      }

      this.indicators.red = false;
      this.indicators.yellow = false;
      this.indicators.blue = false;

      // Unlock the players, grab the next question, and restart the timer.
      this.deck.shift();
    }
  }


  stop() {
    if (this.playing) {
      this.state = GameState.Attract;
    }
  }


  onButton(button: ButtonCode): void {
    this.consoleService.log('Button: ' + button);

    // Restart the inactivity timer each time a button is pressed.
    this.startInactivityTimer();

    if (this.state === GameState.Attract) {
      switch (button)
      {
        case ButtonCode.Red1:
          // Start the game in "easy" mode.
          this.start(false);
          break;

          case ButtonCode.Blue1:
          // Start the game in "hard" mode.
          this.start(true);
          break;

        default:
          break;
      }

    } else if (this.state === GameState.Question) {
      // When the game is running translate the button presses from the GPIO input into player selections.
      switch (button) {
        case ButtonCode.Red1:
          this.onSelection('red');
          break;

        case ButtonCode.Yellow1:
          this.onSelection('yellow');
          break;

        case ButtonCode.Blue1:
          this.onSelection('blue');
          break;

        default:
          break;
      }

    } else if (this.state === GameState.Details) {
      if (this.final) {
        this.stop();
      } else {
        this.nextQuestion();
      }
    }
  }


  /* This method is called when the player makes a selection and then proceeds to determine if the selection was valid
   * or invalid.  It is responsible for indicating correct and incorrect answers. */
  onSelection(color: string): void {
    if (this.playing) {
      // Show the indicator the correct/incorrect answers.
      switch (color)
      {
        case 'red':
          this.indicators.red = true;
          this.consoleService.log('Red pressed.');
          break;

        case 'yellow':
          this.indicators.yellow = true;
          this.consoleService.log('Yellow pressed.');
          break;

        case 'blue':
          this.indicators.blue = true;
          this.consoleService.log('Blue pressed.');
          break;
      }

      if (this.current.answer === color) {
        // Correct answer!
        this.play('correct');
        this.state = GameState.Correct;

        setTimeout(() => {
          // After a second, switch to showing the details about the correct answer.
          if (this.state === GameState.Correct) {
            this.state = GameState.Details;

            // If the details screen is still up after 15 seconds, the customer has requested that the next question be
            // shown.
            setTimeout(() => {
              // But if this was the last question, then go back to the attract screen.
              if (this.final) {
                this.stop();
              } else if (this.state === GameState.Details) {
                this.nextQuestion();
              }
            }, this.detailTime);
          }
        }, 1200);

      } else {
        this.play('wrong');
      }
    }
  }
}
